/**
 * Sticker collection — the SPOTLIGHT STAGE (owner-picked redesign, 2026-07-07).
 * The stickers ARE the show: one sticker is always front-and-center, huge, with
 * its rarity ring and story, and everything else supports that stage —
 *   • TYPE tabs (the only navigation): Skill 🏅 / Seasonal 🎁 / Secret 🔮 — the
 *     owner taxonomy of HOW stickers are earned (achievements / events / the
 *     wheel & other random finds). There is NO "regular" class.
 *   • the STAGE: big art + name + rarity + series + description, with prev/next
 *     chevrons; locked stickers show their price and a Buy action (server
 *     enforces price + balance), owned ones equip straight from the stage;
 *   • two EQUIP SOCKETS ("your ball"): always visible, showing what's equipped
 *     now — tap a socket while an owned sticker is spotlit to equip it there;
 *   • the FILMSTRIP: a snap-scrolling thumbnail rail of the active type — tap
 *     any thumb to rotate it into the spotlight (auto-centers the selection).
 *
 * Presentational + thin orchestration: screen-local UI state only; talks to the
 * backend ONLY through the {@link GameServices} facade. The SERVER is the source
 * of truth for wallet/ownership/prices — this screen never debits tokens; it
 * calls {@link GameServices.buySticker} / {@link GameServices.equipSticker} and
 * re-reads the authoritative profile/ownership afterwards.
 *
 * NAMED-ONLY: anonymous callers see the "log in to collect" tease via
 * {@link RequiresAccountError}; the parent only mounts this for named users, but
 * we degrade gracefully anyway.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useT } from './i18n';
import { canAfford, stickerPrice } from '../core/economy';
import type { GameServices } from '../services/types';
import { RequiresAccountError } from '../services/types';
import type { StickerCatalogItem, UserProfile } from '../services/dataModel';

interface StickerShopProps {
  /** The tier-aware data facade. Buy/equip/profile calls are NAMED-ONLY. */
  services: GameServices;
  /** Return to the previous screen (parent owns navigation). */
  onBack: () => void;
  /** When true (inside the POPSTAR tab shell) suppress this screen's own header. */
  embedded?: boolean;
  /** Notify the parent that the token balance changed (a buy), to refresh chrome. */
  onEconomyChange?: () => void;
}

/** A catalog item paired with the live ownership / equip state the UI draws from. */
interface ShopEntry {
  item: StickerCatalogItem;
  owned: boolean;
  equippedSlot: 1 | 2 | null;
  /** True when the user could buy it right now (locked + affordable). */
  affordable: boolean;
}

/** The three collectible classes (owner taxonomy: HOW a sticker is earned). */
const TYPE_TABS: {
  id: StickerCatalogItem['type'];
  label: string;
  icon: string;
  hintKey: string;
  emptyKey: string;
}[] = [
  { id: 'skill', label: 'Skill', icon: '🏅', hintKey: 'shop.hintSkill', emptyKey: 'shop.emptySkill' },
  { id: 'seasonal', label: 'Seasonal', icon: '🎁', hintKey: 'shop.hintSeasonal', emptyKey: 'shop.emptySeasonal' },
  { id: 'secret', label: 'Secret', icon: '🔮', hintKey: 'shop.hintSecret', emptyKey: 'shop.emptySecret' },
];

/** i18n key for a type tab's label ('seasonal' predates the taxonomy rename). */
function typeLabelKey(id: StickerCatalogItem['type']): string {
  return 'shop.' + (id === 'seasonal' ? 'seasonalType' : id);
}

/** A friendlier label for a raw series code (e.g. "001" → "Series 1"). */
function prettyCode(prefix: string, code: string): string {
  const n = Number(code);
  return Number.isFinite(n) && String(n) !== code ? `${prefix} ${n}` : code;
}

export function StickerShop({ services, onBack, embedded = false, onEconomyChange }: StickerShopProps) {
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsAccount, setNeedsAccount] = useState(false);

  const [catalog, setCatalog] = useState<StickerCatalogItem[]>([]);
  const [owned, setOwned] = useState<Set<string>>(new Set());
  const [profile, setProfile] = useState<UserProfile | null>(null);

  // Which class ALBUM is open (null = the album shelf, the browse root).
  const [classView, setClassView] = useState<StickerCatalogItem['type'] | null>(null);
  // The sticker on stage (by id). Auto-picked per class; null only when empty.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'buy' | 'equip1' | 'equip2'>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const stripRef = useRef<HTMLDivElement | null>(null);

  const tokens = profile?.tokens ?? 0;

  /** Pull the catalog + ownership + profile together (named-only network reads). */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNeedsAccount(false);
    try {
      const [cat, own, prof] = await Promise.all([
        services.getStickerCatalog(),
        services.getOwnedStickers(),
        services.getProfile(),
      ]);
      setCatalog(cat);
      setOwned(new Set(own));
      setProfile(prof);
    } catch (err) {
      if (err instanceof RequiresAccountError) {
        setNeedsAccount(true);
      } else {
        setError(err instanceof Error ? err.message : 'Could not load the shop.');
      }
    } finally {
      setLoading(false);
    }
  }, [services]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Which slot (if any) a sticker id is currently equipped into. */
  const slotOf = useCallback(
    (id: string): 1 | 2 | null => {
      if (!profile) return null;
      if (profile.equipped.slot1 === id) return 1;
      if (profile.equipped.slot2 === id) return 2;
      return null;
    },
    [profile],
  );

  /** The open class's entries, series/num-sorted — the filmstrip order. */
  const entries = useMemo<ShopEntry[]>(
    () =>
      classView === null
        ? []
        : catalog
            .filter((item) => item.type === classView)
            .sort((a, b) => a.series.localeCompare(b.series) || a.num - b.num)
            .map((item) => {
              const isOwned = owned.has(item.id);
              return {
                item,
                owned: isOwned,
                equippedSlot: slotOf(item.id),
                affordable: !isOwned && canAfford(tokens, stickerPrice(item.rarity)),
              };
            }),
    [catalog, classView, owned, slotOf, tokens],
  );

  /** Collection progress for the open class. */
  const progress = useMemo(
    () => ({ owned: entries.filter((e) => e.owned).length, total: entries.length }),
    [entries],
  );

  /**
   * Per-type OWNED tallies for the album covers. Deliberately no "out of total"
   * — retired seasonal/event stickers can be permanently unobtainable, so a
   * denominator would read as an impossible checklist (owner spec).
   */
  const typeCounts = useMemo(() => {
    const counts: Record<StickerCatalogItem['type'], number> = {
      skill: 0,
      seasonal: 0,
      secret: 0,
    };
    for (const item of catalog) {
      if (owned.has(item.id) && counts[item.type] !== undefined) counts[item.type] += 1;
    }
    return counts;
  }, [catalog, owned]);

  /**
   * The staged entry. When the current selection isn't in this tab (tab switch,
   * fresh load), fall back to the most SHOWABLE entry: equipped → owned → first.
   */
  const staged = useMemo<ShopEntry | null>(() => {
    if (entries.length === 0) return null;
    return (
      entries.find((e) => e.item.id === selectedId) ??
      entries.find((e) => e.equippedSlot !== null) ??
      entries.find((e) => e.owned) ??
      entries[0]
    );
  }, [entries, selectedId]);

  /** Auto-center the staged thumb in the filmstrip whenever the stage rotates. */
  useEffect(() => {
    if (!staged || !stripRef.current) return;
    const el = stripRef.current.querySelector<HTMLElement>(
      `[data-id="${CSS.escape(staged.item.id)}"]`,
    );
    el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [staged]);

  /** Rotate the stage to the previous/next sticker in the filmstrip order. */
  const rotate = useCallback(
    (dir: -1 | 1) => {
      if (!staged || entries.length === 0) return;
      const idx = entries.findIndex((e) => e.item.id === staged.item.id);
      const next = entries[(idx + dir + entries.length) % entries.length];
      setSelectedId(next.item.id);
    },
    [staged, entries],
  );

  /** Open a class album (staged falls back to its most showable entry). */
  const openClass = useCallback((id: StickerCatalogItem['type']) => {
    setClassView(id);
    setSelectedId(null);
  }, []);

  /** Back to the album shelf. */
  const closeClass = useCallback(() => {
    setClassView(null);
    setSelectedId(null);
  }, []);

  /** Show a transient confirmation toast. */
  const toast = useCallback((msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash((cur) => (cur === msg ? null : cur)), 2400);
  }, []);

  /** Buy the staged sticker with tokens; server enforces price + balance. */
  const onBuy = useCallback(async () => {
    if (!staged || staged.owned) return;
    setBusy('buy');
    setError(null);
    try {
      await services.buySticker(staged.item.id);
      toast(`Bought ${staged.item.title}!`);
      await load(); // re-read authoritative ownership + token balance.
      onEconomyChange?.(); // tokens changed — refresh the shell's chrome card.
    } catch (err) {
      if (err instanceof RequiresAccountError) setNeedsAccount(true);
      else setError(err instanceof Error ? err.message : 'Purchase failed.');
    } finally {
      setBusy(null);
    }
  }, [staged, services, toast, load, onEconomyChange]);

  /** Equip the staged (owned) sticker into a socket; server checks ownership. */
  const onEquip = useCallback(
    async (slot: 1 | 2) => {
      if (!staged || !staged.owned || staged.equippedSlot === slot) return;
      setBusy(slot === 1 ? 'equip1' : 'equip2');
      setError(null);
      try {
        await services.equipSticker(slot, staged.item.id);
        toast(`Equipped to slot ${slot}.`);
        await load();
      } catch (err) {
        if (err instanceof RequiresAccountError) setNeedsAccount(true);
        else setError(err instanceof Error ? err.message : 'Could not equip.');
      } finally {
        setBusy(null);
      }
    },
    [staged, services, toast, load],
  );

  /** The catalog row equipped in a slot (any type — sockets show the whole ball). */
  const equippedIn = useCallback(
    (slot: 1 | 2): StickerCatalogItem | null => {
      const id = slot === 1 ? profile?.equipped.slot1 : profile?.equipped.slot2;
      if (!id) return null;
      return catalog.find((s) => s.id === id) ?? null;
    },
    [profile, catalog],
  );

  const activeTab = TYPE_TABS.find((tab) => tab.id === classView) ?? TYPE_TABS[0];

  return (
    <div className={'screen shop' + (embedded ? ' is-embedded' : '')}>
      <style>{SHOP_CSS}</style>

      {!embedded && (
        <header className="shop-head">
          <button className="btn btn-ghost shop-back" onClick={onBack}>
            ‹ Back
          </button>
          <h1 className="shop-title">{t('shop.title')}</h1>
          <div className="shop-wallet" title="Token balance">
            <span className="token-coin" aria-hidden="true">
              ◉
            </span>
            <span className="token-amount">{tokens.toLocaleString()}</span>
          </div>
        </header>
      )}

      {needsAccount ? (
        <section className="panel tease">
          <h2>Log in to collect</h2>
          <p className="tease-copy">
            Stickers, tokens and the shop unlock with a free account. Anonymous play
            keeps the game and the leaderboard — sign up to start collecting.
          </p>
        </section>
      ) : loading ? (
        <p className="shop-status">Loading the shop…</p>
      ) : error && catalog.length === 0 ? (
        <section className="panel">
          <p className="shop-error">{error}</p>
          <button className="btn btn-ghost" onClick={() => void load()}>
            Retry
          </button>
        </section>
      ) : (
        <>
          {classView === null ? (
            /* ---- THE ALBUM SHELF: navigation IS the showcase. ---- */
            <div className="shop-albums">
              {TYPE_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={'shop-album is-' + tab.id}
                  onClick={() => openClass(tab.id)}
                >
                  <span className="shop-album-icon" aria-hidden>
                    {tab.icon}
                  </span>
                  <span className="shop-album-name">{t(typeLabelKey(tab.id), tab.label)}</span>
                  <span className="shop-album-hint">{t(tab.hintKey)}</span>
                  <span className="shop-album-progress">
                    <b>{typeCounts[tab.id]}</b> {t('shop.collected')}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <>
              {/* Crumb: back to the shelf + which album is open + owned tally
                  (no denominator — retired stickers make totals misleading). */}
              <div className="shop-crumb">
                <button className="shop-crumb-back" type="button" onClick={closeClass}>
                  ‹ {t('shop.types')}
                </button>
                <span className="shop-crumb-label">
                  <span aria-hidden>{activeTab.icon}</span>{' '}
                  {t(typeLabelKey(activeTab.id), activeTab.label)}
                </span>
                <span className="shop-progress-count">
                  {progress.owned} {t('shop.collected')}
                </span>
              </div>

              {staged ? (
            <>
              {/* ---- THE STAGE ---- */}
              <section className="spot-stage" aria-live="polite">
                <button
                  className="spot-chevron"
                  type="button"
                  aria-label="Previous sticker"
                  disabled={entries.length < 2}
                  onClick={() => rotate(-1)}
                >
                  ‹
                </button>

                <div className="spot-center">
                  <div
                    className={
                      'spot-art rarity-' +
                      staged.item.rarity +
                      (staged.owned ? ' owned' : ' locked')
                    }
                  >
                    {staged.item.imageUrl ? (
                      <img src={staged.item.imageUrl} alt={staged.item.title} draggable={false} />
                    ) : (
                      <span className="spot-art-ph" aria-hidden>
                        {staged.owned ? staged.item.num : '?'}
                      </span>
                    )}
                    {!staged.owned && (
                      <span className="spot-lock" aria-label="locked">
                        🔒
                      </span>
                    )}
                    {staged.equippedSlot && (
                      <span className="sticker-badge" aria-hidden>
                        {staged.equippedSlot}
                      </span>
                    )}
                  </div>

                  <h2 className="spot-title">{staged.item.title}</h2>
                  <p className={'spot-rarity is-' + staged.item.rarity}>{staged.item.rarity}</p>
                  <p className="spot-path">
                    {prettyCode('Series', staged.item.series)} · #{staged.item.num}
                  </p>
                  <p className="spot-desc">{staged.item.desc}</p>

                  {/* Stage actions: Buy when locked; sockets handle equipping. */}
                  {!staged.owned && (
                    <div className="spot-actions">
                      <button
                        className="btn btn-primary spot-buy"
                        disabled={busy !== null || !staged.affordable}
                        onClick={() => void onBuy()}
                      >
                        {busy === 'buy' ? (
                          'Buying…'
                        ) : (
                          <>
                            {t('shop.buy')}
                            <span className="spot-buy-price">
                              <span className="token-coin" aria-hidden>
                                ◉
                              </span>
                              {stickerPrice(staged.item.rarity).toLocaleString()}
                            </span>
                          </>
                        )}
                      </button>
                      {!staged.affordable && (
                        <p className="shop-note">Not enough tokens — spin the wheel to earn more.</p>
                      )}
                    </div>
                  )}
                </div>

                <button
                  className="spot-chevron"
                  type="button"
                  aria-label="Next sticker"
                  disabled={entries.length < 2}
                  onClick={() => rotate(1)}
                >
                  ›
                </button>
              </section>

              {/* ---- EQUIP SOCKETS ("your ball") ---- */}
              <section className="spot-sockets" aria-label={t('shop.equippedOn')}>
                <span className="spot-sockets-label">
                  {staged.owned ? t('shop.tapToEquip') : t('shop.equippedOn')}
                </span>
                <div className="spot-sockets-row">
                  {([1, 2] as const).map((slot) => {
                    const inSlot = equippedIn(slot);
                    const holdsStaged = staged.equippedSlot === slot;
                    const equipBusy = busy === (slot === 1 ? 'equip1' : 'equip2');
                    return (
                      <button
                        key={slot}
                        type="button"
                        className={
                          'spot-socket' +
                          (inSlot ? ' filled' : '') +
                          (holdsStaged ? ' holds-staged' : '') +
                          (staged.owned && !holdsStaged ? ' can-take' : '')
                        }
                        disabled={busy !== null || !staged.owned || holdsStaged}
                        title={inSlot ? inSlot.title : `Slot ${slot}`}
                        aria-label={
                          holdsStaged
                            ? `Slot ${slot} — holds ${staged.item.title}`
                            : `Equip ${staged.item.title} to slot ${slot}`
                        }
                        onClick={() => void onEquip(slot)}
                      >
                        <span className="spot-socket-num" aria-hidden>
                          {slot}
                        </span>
                        {equipBusy ? (
                          <span className="spot-socket-ph" aria-hidden>
                            …
                          </span>
                        ) : inSlot ? (
                          inSlot.imageUrl ? (
                            <img src={inSlot.imageUrl} alt="" draggable={false} />
                          ) : (
                            <span className="spot-socket-ph" aria-hidden>
                              {inSlot.num}
                            </span>
                          )
                        ) : (
                          <span className="spot-socket-ph empty" aria-hidden>
                            +
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* ---- THE FILMSTRIP ---- */}
              <div className="spot-strip" ref={stripRef} role="listbox" aria-label="Stickers">
                {entries.map((e) => (
                  <button
                    key={e.item.id}
                    type="button"
                    data-id={e.item.id}
                    role="option"
                    aria-selected={staged.item.id === e.item.id}
                    aria-label={`${e.item.title}${e.owned ? '' : ' (locked)'}`}
                    className={
                      'spot-thumb rarity-' +
                      e.item.rarity +
                      (e.owned ? ' owned' : ' locked') +
                      (staged.item.id === e.item.id ? ' selected' : '')
                    }
                    onClick={() => setSelectedId(e.item.id)}
                  >
                    {e.item.imageUrl ? (
                      <img src={e.item.imageUrl} alt="" draggable={false} />
                    ) : (
                      <span className="spot-thumb-ph" aria-hidden>
                        {e.owned ? e.item.num : '?'}
                      </span>
                    )}
                    {!e.owned && (
                      <span className="sticker-lock" aria-hidden>
                        🔒
                      </span>
                    )}
                    {e.equippedSlot && (
                      <span className="sticker-badge" aria-hidden>
                        {e.equippedSlot}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </>
              ) : (
                /* Empty class: explain how THIS kind of sticker is earned. */
                <section className="panel spot-empty">
                  <p className="spot-empty-icon" aria-hidden>
                    {activeTab.icon}
                  </p>
                  <p className="shop-status">{t(activeTab.emptyKey)}</p>
                </section>
              )}
            </>
          )}

          {/* A non-fatal reload error (we kept the last catalog on screen). */}
          {error && catalog.length > 0 && <p className="shop-error">{error}</p>}
        </>
      )}

      {flash && (
        <div className="shop-toast" role="status">
          {flash}
        </div>
      )}
    </div>
  );
}

/**
 * Screen-scoped styles for the spotlight stage (nested under `.shop` so nothing
 * leaks). Shared primitives (`.shop-head`, `.shop-tabs`, `.sticker-lock`,
 * `.sticker-badge`, the `.rarity-*` rings, `.shop-toast`, CSS vars) come from
 * src/index.css; the stage / sockets / filmstrip layout lives here.
 */
const SHOP_CSS = `
/* ---- THE ALBUM SHELF: three class covers (no pills — big square cards). ---- */
.shop .shop-albums {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  width: min(560px, 94vw);
}
.shop .shop-album {
  --album-accent: var(--accent);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  font: inherit;
  text-align: center;
  color: var(--text);
  padding: 18px 14px 16px;
  background: var(--panel);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: transform var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease),
    box-shadow var(--t-fast) var(--ease);
}
.shop .shop-album.is-skill { --album-accent: var(--gold); }
.shop .shop-album.is-seasonal { --album-accent: var(--accent-2); }
.shop .shop-album.is-secret { --album-accent: var(--accent); }
.shop .shop-album:hover { transform: translateY(-3px); border-color: var(--album-accent); }
.shop .shop-album.is-skill:hover { box-shadow: var(--shadow-2), 0 0 24px rgba(255, 209, 102, 0.22); }
.shop .shop-album.is-seasonal:hover { box-shadow: var(--shadow-2), 0 0 24px rgba(255, 79, 157, 0.22); }
.shop .shop-album.is-secret:hover { box-shadow: var(--shadow-2), 0 0 24px var(--accent-glow); }
.shop .shop-album-icon { font-size: 34px; line-height: 1; }
.shop .shop-album-name {
  font-size: 14px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.shop .shop-album-hint {
  font-size: 11px;
  color: var(--muted);
  line-height: 1.35;
  min-height: 30px;
}
.shop .shop-album-progress {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  margin-top: 2px;
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.07em;
}
.shop .shop-album-progress b {
  font-size: 22px;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
  color: var(--album-accent);
  line-height: 1;
}

/* ---- Crumb: back to the shelf + open album + progress. ---- */
.shop .shop-crumb {
  display: flex;
  align-items: center;
  gap: 12px;
  width: min(560px, 94vw);
}
.shop .shop-crumb-back {
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-dim);
  background: var(--panel);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-sm);
  padding: 7px 12px;
  cursor: pointer;
  transition: color var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease);
}
.shop .shop-crumb-back:hover { color: var(--text); border-color: var(--glass-border-strong); }
.shop .shop-crumb-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.shop .shop-crumb .shop-progress-count { margin-left: auto; }
.shop .shop-progress-count {
  color: var(--gold);
  font-weight: 700;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* ---- THE STAGE: chevron · big art + meta · chevron ---- */
.shop .spot-stage {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 6px;
  width: min(560px, 94vw);
}
.shop .spot-chevron {
  font: inherit;
  font-size: 30px;
  font-weight: 800;
  line-height: 1;
  color: var(--text-dim);
  background: var(--panel);
  border: 1px solid var(--glass-border);
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  cursor: pointer;
  padding: 0 0 4px;
  transition: color var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease);
}
.shop .spot-chevron:hover:not(:disabled) { color: var(--text); border-color: var(--accent); }
.shop .spot-chevron:disabled { opacity: 0.35; cursor: default; }

.shop .spot-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 0;
  text-align: center;
}
.shop .spot-art {
  position: relative;
  width: min(200px, 46vw);
  aspect-ratio: 1;
  border-radius: 26px;
  display: grid;
  place-items: center;
  background:
    radial-gradient(80% 80% at 50% 30%, var(--accent-tint), transparent 70%),
    var(--panel-2);
  overflow: hidden;
  margin-bottom: 8px;
  /* the rarity ring (from .rarity-*) is the star — add a soft stage glow under it */
  filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.4));
}
.shop .spot-art img { width: 100%; height: 100%; object-fit: contain; }
.shop .spot-art-ph {
  font-size: 64px;
  font-weight: 900;
  color: var(--muted);
  opacity: 0.6;
}
.shop .spot-art.locked img { filter: grayscale(0.8) brightness(0.6); }
.shop .spot-lock {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-size: 40px;
  background: rgba(8, 6, 16, 0.45);
}
.shop .spot-art .sticker-badge { top: 8px; right: 8px; width: 24px; height: 24px; font-size: 13px; }

.shop .spot-title {
  margin: 0;
  font-size: 22px;
  font-weight: 900;
  color: var(--text);
  text-transform: none;
  letter-spacing: 0;
}
.shop .spot-rarity {
  margin: 0;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.14em;
}
.shop .spot-rarity.is-common { color: #96aaff; }
.shop .spot-rarity.is-uncommon { color: #2ecc71; }
.shop .spot-rarity.is-rare { color: #4cc9f0; }
.shop .spot-rarity.is-epic { color: #c45cff; }
.shop .spot-rarity.is-legendary { color: var(--gold); text-shadow: 0 0 14px rgba(255, 209, 102, 0.4); }
.shop .spot-path {
  margin: 0;
  font-size: 12px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
.shop .spot-desc {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--text-dim);
  line-height: 1.45;
  max-width: 340px;
}
.shop .spot-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
}
.shop .spot-buy {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 11px 22px;
  font-size: 15px;
}
.shop .spot-buy-price {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-variant-numeric: tabular-nums;
}
.shop .shop-note { margin: 0; font-size: 12px; color: var(--muted); }

/* ---- EQUIP SOCKETS ---- */
.shop .spot-sockets {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: min(560px, 94vw);
  padding: 12px 16px;
  background: var(--panel);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius);
}
.shop .spot-sockets-label {
  font-size: 11px;
  font-weight: 800;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.shop .spot-sockets-row { display: flex; gap: 18px; }
.shop .spot-socket {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  padding: 0;
  background: var(--panel-2);
  border: 2px dashed var(--glass-border-strong);
  overflow: hidden;
  cursor: pointer;
  transition: border-color var(--t-fast) var(--ease), box-shadow var(--t-fast) var(--ease),
    transform var(--t-fast) var(--ease);
}
.shop .spot-socket img { width: 100%; height: 100%; object-fit: cover; }
.shop .spot-socket.filled { border-style: solid; }
.shop .spot-socket.holds-staged {
  border-color: var(--gold);
  box-shadow: 0 0 20px rgba(255, 209, 102, 0.35);
  cursor: default;
}
.shop .spot-socket.can-take:not(:disabled) { border-color: var(--accent); }
.shop .spot-socket.can-take:not(:disabled):hover { transform: translateY(-2px); }
/* Invite the tap while an owned sticker is staged and this socket can take it. */
.shop .spot-socket.can-take:not(:disabled) { animation: socket-pulse 1.6s var(--ease) infinite; }
@keyframes socket-pulse {
  0%, 100% { box-shadow: 0 0 0 0 var(--accent-glow); }
  50% { box-shadow: 0 0 0 7px transparent; }
}
@media (prefers-reduced-motion: reduce) {
  .shop .spot-socket.can-take:not(:disabled) { animation: none; }
}
.shop .spot-socket:disabled { cursor: default; }
.shop .spot-socket-num {
  position: absolute;
  top: 2px;
  left: 6px;
  z-index: 1;
  font-size: 10px;
  font-weight: 900;
  color: var(--muted);
}
.shop .spot-socket-ph { font-size: 22px; font-weight: 800; color: var(--text-dim); }
.shop .spot-socket-ph.empty { color: var(--muted); opacity: 0.6; }

/* ---- THE FILMSTRIP ---- */
.shop .spot-strip {
  display: flex;
  gap: 10px;
  width: min(560px, 94vw);
  padding: 6px 4px 10px;
  overflow-x: auto;
  scroll-snap-type: x proximity;
  scrollbar-width: thin;
}
.shop .spot-thumb {
  position: relative;
  flex: 0 0 auto;
  width: 72px;
  height: 72px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  padding: 0;
  background: var(--panel-2);
  border: 2px solid transparent;
  overflow: hidden;
  cursor: pointer;
  scroll-snap-align: center;
  transition: transform var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease);
}
.shop .spot-thumb img { width: 100%; height: 100%; object-fit: cover; }
.shop .spot-thumb:hover { transform: translateY(-2px); }
.shop .spot-thumb.selected {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-tint);
}
.shop .spot-thumb.locked { opacity: 0.7; }
.shop .spot-thumb-ph { font-size: 20px; font-weight: 800; color: var(--muted); }
.shop .spot-thumb .sticker-lock { font-size: 18px; }

/* ---- Empty class ---- */
.shop .spot-empty {
  width: min(560px, 94vw);
  text-align: center;
}
.shop .spot-empty-icon { font-size: 40px; margin: 4px 0 8px; }

/* Narrow phones: shrink the chevrons so the art keeps the room. */
@media (max-width: 420px) {
  .shop .spot-chevron { width: 34px; height: 34px; font-size: 24px; }
  .shop .spot-sockets-row { gap: 12px; }
}
`;
